import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';
import { getGeoLocation, submitLead, buildLeadPayload, handleLeadResponse } from '../services/apiClient';
import { validateEmail } from '../utils/validation';
import ContactModal from '../components/ContactModal';
import TestimonialSlider from '../components/TestimonialSlider';
import ServicesSlider from '../components/ServicesSlider';
import WhyChooseUsSlider from '../components/WhyChooseUsSlider';
import WhyYouNeedSlider from '../components/WhyYouNeedSlider';
import MobileSlider from '../components/MobileSlider';
import {
  Shield,
  FileText,
  Building,
  Search,
  ShoppingBag,
  CheckCircle,
  Clock,
  Users,
  Award,
  TrendingUp,
  Star,
  Lock,
  DollarSign,
  AlertCircle,
} from 'lucide-react';

const Home = () => {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [displayCountry, setDisplayCountry] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: '',
  });

  // Fetch geolocation on component mount
  React.useEffect(() => {
    const fetchGeoLocation = async () => {
      const data = await getGeoLocation();
      if (data.isSuccess && data.data) {
        setGeoData(data);
        setDisplayCountry(data.data.countryCode || '');
        // Set default country code based on geolocation
        const dialCode = getDialCodeFromCountry(data.data.countryCode);
        setFormData(prev => ({ ...prev, countryCode: dialCode }));
      }
    };
    fetchGeoLocation();
  }, []);

  // Helper function to get dial code from country code
  const getDialCodeFromCountry = (countryCode) => {
    const dialCodes = {
      'US': '+1', 'CA': '+1', 'GB': '+44', 'AU': '+61', 'IN': '+91',
      'PK': '+92', 'NL': '+31', 'DE': '+49', 'FR': '+33', 'ES': '+34',
      'IT': '+39', 'BR': '+55', 'MX': '+52', 'JP': '+81', 'CN': '+86',
    };
    return dialCodes[countryCode] || '+1';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!validateEmail(formData.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone (must be numeric and between 7-15 digits)
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number (7-15 digits).',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build payload using the shared function
      const payload = buildLeadPayload(formData, geoData);

      // Submit lead
      const response = await submitLead(payload);

      // Handle response
      const success = handleLeadResponse(response, (errorMsg) => {
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'destructive',
        });
      });

      if (success) {
        // Form will redirect, but show success message briefly
        toast({
          title: 'Success!',
          description: 'Redirecting to your application...',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: Shield,
      title: 'Trademark Registration',
      description: 'Protect your brand identity with comprehensive trademark registration services.',
      link: '/trademark-registration',
      backgroundImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800',
    },
    {
      icon: FileText,
      title: 'Copyright Registration',
      description: 'Secure your creative works with professional copyright registration.',
      link: '/copyright-registration',
      backgroundImage: 'https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800',
    },
    {
      icon: Building,
      title: 'Company Formation',
      description: 'Establish your business with seamless company formation assistance.',
      link: '/company-formation',
      backgroundImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
    },
    {
      icon: Search,
      title: 'Trademark Search',
      description: 'Comprehensive trademark search to ensure your brand is unique.',
      link: '/trademark-search',
      backgroundImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800',
    },
    {
      icon: ShoppingBag,
      title: 'Amazon Brand Registry',
      description: 'Protect your brand on Amazon with our expert brand registry services.',
      link: '/amazon-brand-registry',
      backgroundImage: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800',
    },
  ];

  const steps = [
    {
      title: 'Information Collection',
      description: 'We gather all necessary details about your brand and requirements to ensure accurate filing.',
    },
    {
      title: 'Research and Search Report',
      description: 'Our experts conduct thorough research and provide a comprehensive search report to identify potential conflicts.',
    },
    {
      title: 'Application and Submission',
      description: 'We prepare and submit your application to the USPTO with all required documentation for swift approval.',
    },
  ];

  const packages = [
    {
      name: 'BASIC',
      price: '$45',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
      ],
    },
    {
      name: 'DELUXE',
      price: '$145',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager',
      ],
      popular: true,
    },
    {
      name: 'PREMIUM',
      price: '$299',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review (by a TM Expert)',
        'Basic Free Trademark Search',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured',
        'Refusal Risk Meter',
        'Case filing service with complete paperwork',
      ],
    },
    {
      name: 'ELITE',
      price: '$599',
      priceNote: '*(Govt. filing fee not included)',
      features: [
        'Case Review & Filing (U.S Registered IP Attorney)',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Trademark Secured',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
      ],
    },
  ];

  const whyYouNeedReasons = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Secure exclusive rights to your brand name, logo, and intellectual property nationwide.',
    },
    {
      icon: Lock,
      title: 'Prevent Infringement',
      description: 'Stop competitors from using similar marks that could confuse customers and dilute your brand.',
    },
    {
      icon: DollarSign,
      title: 'Increase Business Value',
      description: 'A registered trademark is a valuable asset that increases your company\'s worth and credibility.',
    },
    {
      icon: AlertCircle,
      title: 'Avoid Legal Issues',
      description: 'Protect yourself from costly lawsuits and disputes over brand ownership.',
    },
  ];

  const usps = [
    {
      icon: Award,
      title: 'Expert Legal Team',
      description: 'Our experienced attorneys and trademark specialists have successfully registered thousands of trademarks across all industries.',
      backgroundImage: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800',
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'We prioritize efficiency without compromising quality, ensuring your applications are filed promptly and accurately.',
      backgroundImage: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800',
    },
    {
      icon: Users,
      title: 'Personalized Support',
      description: 'Every client receives dedicated attention with tailored solutions to meet their specific brand protection needs.',
      backgroundImage: 'https://images.unsplash.com/photo-1560264280-88b68371db39?w=800',
    },
    {
      icon: CheckCircle,
      title: '98% Approval Rate',
      description: 'Industry-leading success rate with meticulous attention to detail and comprehensive trademark searches.',
      backgroundImage: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
    },
    {
      icon: TrendingUp,
      title: 'Affordable Pricing',
      description: 'Transparent pricing with no hidden fees. Professional legal services accessible to businesses of all sizes.',
      backgroundImage: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800',
    },
  ];

  const stats = [
    {
      number: '10,000+',
      label: 'Trademarks Registered',
      description: 'Successfully registered trademarks for businesses across the United States',
    },
    {
      number: '98%',
      label: 'Success Rate',
      description: 'Industry-leading approval rate for trademark applications filed',
    },
    {
      number: '15+ Years',
      label: 'Experience',
      description: 'Proven track record in intellectual property protection services',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStartup Inc.',
      service: 'Trademark Registration',
      text: 'Fine Trademark made the trademark registration process incredibly smooth. Their team was professional, responsive, and helped us secure our brand identity quickly.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=200&h=200&fit=crop',
    },
    {
      name: 'Michael Chen',
      company: 'Creative Studio LLC',
      service: 'Copyright Registration',
      text: 'Excellent service for copyright registration. They handled everything efficiently and kept us informed throughout the entire process.',
      rating: 5,
      image: 'https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?w=200&h=200&fit=crop',
    },
    {
      name: 'Emily Rodriguez',
      company: 'Fashion Boutique',
      service: 'Amazon Brand Registry',
      text: 'Thanks to Fine Trademark, we successfully registered our brand on Amazon. Their expertise in brand protection is unmatched.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=200&h=200&fit=crop',
    },
    {
      name: 'David Thompson',
      company: 'Tech Innovations Co.',
      service: 'Company Formation',
      text: 'Setting up our company was hassle-free with Fine Trademark. They guided us through every step and ensured all legal requirements were met.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1652471943570-f3590a4e52ed?w=200&h=200&fit=crop',
    },
    {
      name: 'Jennifer Lee',
      company: 'Artisan Goods',
      service: 'Trademark Search',
      text: 'The comprehensive trademark search saved us from potential legal issues. Their detailed report gave us confidence in our brand choice.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=200&h=200&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to approval. However, we ensure your application is filed within 24-48 hours of receiving all necessary information.',
    },
    {
      question: 'What is included in the $45 trademark registration offer?',
      answer: 'Our $45 special offer includes a basic trademark search, application preparation, and filing with the USPTO. This is a limited-time promotional rate for first-time clients.',
    },
    {
      question: 'Do you handle trademark applications for all industries?',
      answer: 'Yes, we have experience registering trademarks across all industries including technology, fashion, food & beverage, healthcare, and more.',
    },
    {
      question: 'What happens if my trademark application is rejected?',
      answer: 'If your application receives an office action or rejection, our team will work with you to address the issues and resubmit your application at no additional attorney fees.',
    },
    {
      question: 'Can you help with international trademark registration?',
      answer: 'While we specialize in US trademark registration, we can guide you through the Madrid Protocol for international trademark protection in multiple countries.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1E1E1E] via-[#2a2a2a] to-[#1E1E1E] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Form - Shows First on Mobile */}
            <Card className="bg-white/95 backdrop-blur-sm shadow-2xl animate-in fade-in duration-700 order-1 lg:order-2 w-full">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#1E1E1E] mb-2">Start Your Registration Today</h3>
                <p className="text-gray-600 mb-6">Special Offer: Trademark Registration for only $45</p>
                {displayCountry && (
                  <div className="mb-4 p-3 bg-[#E5B80B]/10 rounded-lg">
                    <p className="text-sm text-[#1E1E1E]">
                      <span className="font-semibold">Your Country:</span> {displayCountry}
                    </p>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                      Full Name *
                    </label>
                    <Input
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 border-2 focus:border-[#E5B80B]"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12 border-2 focus:border-[#E5B80B]"
                    />
                  </div>

                  {/* Contact Number with Country Code */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                      Contact Number *
                    </label>
                    <div className="flex gap-3">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                        className="h-12 px-3 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E5B80B] focus:border-[#E5B80B] font-semibold text-[#1E1E1E] w-28 bg-white"
                      >
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+61">🇦🇺 +61</option>
                        <option value="+86">🇨🇳 +86</option>
                        <option value="+81">🇯🇵 +81</option>
                        <option value="+49">🇩🇪 +49</option>
                        <option value="+33">🇫🇷 +33</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+65">🇸🇬 +65</option>
                        <option value="+92">🇵🇰 +92</option>
                        <option value="+31">🇳🇱 +31</option>
                      </select>
                      <Input
                        type="tel"
                        placeholder="Enter phone number (digits only)"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                        required
                        maxLength={15}
                        className="h-12 flex-1 border-2 focus:border-[#E5B80B]"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter numbers only (7-15 digits)</p>
                  </div>

                  {/* Description Field */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                      Description
                    </label>
                    <Textarea
                      placeholder="Tell us about your requirements..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="min-h-28 border-2 focus:border-[#E5B80B] resize-none"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold h-12 text-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Register Your Trademark'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Hero Content - Shows Second on Mobile */}
            <div className="space-y-6 animate-in fade-in duration-700 order-2 lg:order-1">
              <Badge className="bg-[#E5B80B] text-[#1E1E1E] hover:bg-[#E5B80B] text-sm px-4 py-1">
                Special Offer - Limited Time Only
              </Badge>
              <h1 className="page-title text-white leading-tight">
                Protect Your Brand Identity with Confidence
              </h1>
              <p className="body-large text-gray-300">
                Secure your trademark, copyright, and business formation with our expert legal services. We've helped over 10,000 businesses protect their intellectual property across the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  size="lg" 
                  className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-8 text-lg h-14 transition-all duration-300 hover:scale-105"
                  onClick={() => setIsModalOpen(true)}
                >
                  Register Your Trademark
                </Button>
                <Link to="/about-us">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1E1E1E] font-semibold px-8 text-lg h-14 transition-all duration-300">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need Trademark Registration */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4">Why You Need Trademark Registration</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Protect your brand and secure your business future with trademark registration
            </p>
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="lg:hidden">
            <WhyYouNeedSlider reasons={whyYouNeedReasons} />
          </div>

          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-8">
            {whyYouNeedReasons.map((reason, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-6 bg-[#E5B80B]/10 rounded-full">
                    <reason.icon className="h-12 w-12 text-[#E5B80B]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1E1E1E] mb-4">{reason.title}</h3>
                <p className="text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">Our Services</h2>
            <p className="body-large text-gray-600">Comprehensive solutions for all your brand protection needs</p>
          </div>
          <ServicesSlider services={services} />
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#1E1E1E] mb-4 lg:mb-6">How We Work</h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our streamlined three-step process ensures your trademark registration is handled efficiently and professionally.
            </p>
          </div>
          
          {/* Mobile Slider - Only for Mobile/Tablet */}
          <div className="lg:hidden pb-12">
            <MobileSlider items={steps} type="steps" />
          </div>

          {/* Desktop Grid - Only for Desktop */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E5B80B] text-[#1E1E1E] flex items-center justify-center text-3xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E1E1E] mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-[#E5B80B]/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6">Transparent Pricing</h2>
            <p className="body-large max-w-3xl mx-auto">
              Choose the package that best fits your needs. All packages include expert support and comprehensive service.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.popular ? 'border-4 border-[#E5B80B] shadow-2xl scale-105' : 'border-2'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-[#E5B80B] text-[#1E1E1E] text-sm px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="pricing-card p-6">
                  <div className="pricing-header">
                    <h3 className="card-title text-white mb-2">{pkg.name}</h3>
                    <div className="border-t border-white/30 w-12 mx-auto my-2"></div>
                    <span className="text-4xl font-bold">{pkg.price}</span>
                  </div>
                  <div className="pricing-features mt-6">
                    <ul className="space-y-3">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-[#E5B80B] mr-2 mt-0.5 flex-shrink-0" />
                          <span className="body-small text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center mt-4">{pkg.priceNote}</p>
                  <Button 
                    className={`w-full h-12 font-semibold transition-all duration-300 mt-auto ${
                      pkg.popular
                        ? 'bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E]'
                        : 'bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-6">Why Choose Fine Trademark</h2>
            <p className="body-large text-gray-600 max-w-3xl mx-auto">
              We combine legal expertise with personalized service to deliver exceptional results for every client.
            </p>
          </div>
          <WhyChooseUsSlider features={usps} />
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1E1E1E] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Our Track Record</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl lg:text-6xl font-bold text-[#E5B80B] mb-4">{stat.number}</div>
                <h3 className="text-2xl font-semibold mb-3">{stat.label}</h3>
                <p className="text-gray-400">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E5B80B] to-[#c99f09]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E1E1E] mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl text-[#1E1E1E]/90 mb-8 leading-relaxed">
            Don't wait until it's too late. Secure your trademark registration today with our limited-time $45 offer and gain peace of mind knowing your brand is protected.
          </p>
          <Button 
            size="lg" 
            className="bg-[#1E1E1E] hover:bg-[#2a2a2a] text-white font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105"
            onClick={() => setIsModalOpen(true)}
          >
            Get Started Now
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="section-heading mb-4">What Our Clients Say</h2>
          </div>
          <TestimonialSlider testimonials={testimonials} showServiceBadge={true} />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1E1E1E] mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left text-lg font-semibold text-[#1E1E1E] hover:text-[#E5B80B]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1E1E1E] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Secure Your Brand for Just $45</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Limited time offer for new clients. Start your trademark registration journey today and protect what you've built.
          </p>
          <Button 
            size="lg" 
            className="bg-[#E5B80B] hover:bg-[#c99f09] text-[#1E1E1E] font-semibold px-12 text-lg h-14 transition-all duration-300 hover:scale-105"
            onClick={() => setIsModalOpen(true)}
          >
            Register Your Trademark
          </Button>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default Home;
